import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  QueryStateBoundary,
  Select,
  toastError,
  toastSuccess,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import {
  useMapArtDay,
  useSquareArtDays,
  useSquareCatalog,
  useSquareConnect,
  useSquareDisconnect,
  useSquareStatus,
} from "@/api/hooks/useSquare";
import type { SquareArtDay, SquareCatalogItem, SquareStatus } from "@/api/types/square";
import { centsToUsd } from "@/lib/money";

/**
 * Venue-owner page for taking Art Days payments through Square: connect the
 * account, then pick which Square item each date sells. Reached at /square,
 * and Square sends the browser back here after its consent screen.
 */
export function SquarePage() {
  const status = useSquareStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnHandled = useRef(false);

  // Square's OAuth round-trip lands here as ?square=<result>. Toast it once,
  // then strip the param so a refresh doesn't toast again.
  useEffect(() => {
    const result = searchParams.get("square");
    if (!result || returnHandled.current) return;
    returnHandled.current = true;

    if (result === "connected") {
      toastSuccess("Square connected");
    } else if (result === "declined") {
      toastError("Square connection cancelled");
    } else if (result === "error" || result === "state_error") {
      toastError("Square connection failed", "Try again");
    }

    const next = new URLSearchParams(searchParams);
    next.delete("square");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="p-6 sm:p-8 max-w-3xl space-y-6">
      <h1 className="font-display text-2xl font-bold text-text-primary">Square</h1>

      <QueryStateBoundary
        isLoading={status.isLoading}
        error={status.error}
        onRetry={() => void status.refetch()}
      >
        {status.data && (
          <>
            <SquareStatusCard status={status.data} />
            {status.data.connected && <ArtDaysCard />}
          </>
        )}
      </QueryStateBoundary>
    </div>
  );
}

function SquareStatusCard({ status }: { status: SquareStatus }) {
  const connect = useSquareConnect();
  const disconnect = useSquareDisconnect();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  async function handleConnect() {
    try {
      const authorizeUrl = await connect.mutateAsync();
      window.location.assign(authorizeUrl);
    } catch (err) {
      toastError("Couldn't start Square connection", getErrorMessage(err));
    }
  }

  async function handleDisconnect() {
    try {
      await disconnect.mutateAsync();
      toastSuccess("Square disconnected");
    } catch (err) {
      toastError("Couldn't disconnect Square", getErrorMessage(err));
    } finally {
      setConfirmingDisconnect(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Square account</CardTitle>
        <CardDescription>Takes card payments for Art Days on the website.</CardDescription>
      </CardHeader>
      <CardContent>
        {!status.configured && (
          <p className="text-sm text-text-secondary">
            Square isn't set up on the server yet. Will is on it.
          </p>
        )}

        {status.configured && !status.connected && (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-text-secondary">
              Connect your Square account so guests can pay for Art Days online.
            </p>
            <Button
              className="w-full sm:w-auto sm:shrink-0"
              onClick={() => void handleConnect()}
              disabled={connect.isPending || connect.isSuccess}
            >
              {connect.isPending
                ? "Opening Square…"
                : connect.isSuccess
                  ? "Redirecting to Square…"
                  : "Connect Square"}
            </Button>
          </div>
        )}

        {status.connected && (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate font-medium text-text-primary">
                  {status.business_name ?? "Square account"}
                </p>
                <Badge variant="success">Connected</Badge>
              </div>
              {status.connected_at && (
                <p className="mt-1 text-sm text-text-secondary">
                  Connected {formatShortDate(status.connected_at)}
                </p>
              )}
              {confirmingDisconnect && (
                <p className="mt-2 text-sm text-text-secondary">
                  Paid Art Days checkouts stop until you reconnect.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {confirmingDisconnect ? (
                <>
                  <Button
                    variant="danger"
                    onClick={() => void handleDisconnect()}
                    disabled={disconnect.isPending}
                  >
                    {disconnect.isPending ? "Disconnecting…" : "Yes, disconnect"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmingDisconnect(false)}
                    disabled={disconnect.isPending}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => setConfirmingDisconnect(true)}>
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Mounted only while connected, so its catalog and art-days queries are always enabled. */
function ArtDaysCard() {
  const catalog = useSquareCatalog(true);
  const artDays = useSquareArtDays();
  const mapArtDay = useMapArtDay();

  const sessions = useMemo(
    () =>
      [...(artDays.data?.items ?? [])].sort((a, b) =>
        a.session_date.localeCompare(b.session_date),
      ),
    [artDays.data],
  );
  const catalogItems = useMemo(() => catalog.data?.items ?? [], [catalog.data]);
  const catalogIds = useMemo(
    () => new Set(catalogItems.map((item) => item.variation_id)),
    [catalogItems],
  );

  async function handleMap(session: SquareArtDay, variationId: string | null) {
    const dateLabel = formatSessionDate(session.session_date);
    try {
      await mapArtDay.mutateAsync({ id: session.id, variation_id: variationId });
      if (variationId) {
        toastSuccess("Checkout item saved", dateLabel);
      } else {
        toastSuccess("Online checkout turned off", `${dateLabel} is a free sign-up`);
      }
    } catch (err) {
      toastError("Couldn't save that item", getErrorMessage(err));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Art Days</CardTitle>
        <CardDescription>
          Pick the Square item each date sells. Paid checkouts on the website count seats here
          automatically, and so does ringing up that item in Square.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <QueryStateBoundary
          isLoading={artDays.isLoading || catalog.isLoading}
          error={artDays.error ?? catalog.error}
          onRetry={() => {
            void artDays.refetch();
            void catalog.refetch();
          }}
          errorTitle="Unable to load Art Days"
        >
          {sessions.length === 0 ? (
            <p className="text-sm text-text-secondary">No Art Days sessions yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {sessions.map((session) => {
                const dateLabel = formatSessionDate(session.session_date);
                const saving = mapArtDay.isPending && mapArtDay.variables?.id === session.id;

                return (
                  <li
                    key={session.id}
                    className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-text-primary">{dateLabel}</p>
                      <p className="text-sm text-text-secondary">
                        {formatClock(session.start_time)} – {formatClock(session.end_time)} ·{" "}
                        {session.taken} / {session.capacity} seats
                      </p>
                    </div>

                    <div className="w-full sm:w-80 sm:shrink-0">
                      <Select
                        aria-label={`Square item for ${dateLabel}`}
                        value={session.square_variation_id ?? ""}
                        disabled={saving}
                        onChange={(e) => void handleMap(session, e.target.value || null)}
                      >
                        <option value="">No online checkout (free sign-up)</option>
                        {catalogItems.map((item) => (
                          <option key={item.variation_id} value={item.variation_id}>
                            {catalogLabel(item)}
                          </option>
                        ))}
                        {session.square_variation_id !== null &&
                          !catalogIds.has(session.square_variation_id) && (
                            <option value={session.square_variation_id}>
                              Item not in your Square catalog
                            </option>
                          )}
                      </Select>
                      {session.square_price_cents !== null && (
                        <p className="mt-1 text-xs text-text-muted">
                          Checkout price {centsToUsd(session.square_price_cents)}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </QueryStateBoundary>
      </CardContent>
    </Card>
  );
}

/** "Paint & Sip - Regular ($35.00)"; the variation is dropped when it's blank or "Regular". */
function catalogLabel(item: SquareCatalogItem): string {
  const variation = item.variation_name.trim();
  const base =
    variation === "" || variation === "Regular"
      ? item.item_name
      : `${item.item_name} - ${variation}`;
  return item.price_cents === null ? base : `${base} (${centsToUsd(item.price_cents)})`;
}

/** "Sat, Nov 8". Built from parts so the date isn't shifted by a UTC parse. */
function formatSessionDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** "10:00 AM" from a "HH:MM" string. */
function formatClock(hhmm: string): string {
  const [hourStr, minuteStr] = hhmm.split(":");
  const hour24 = Number(hourStr);
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minuteStr.padStart(2, "0")} ${period}`;
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
